import {
  createContext,
  useCallback,
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { addBreadcrumb } from '@sentry/react';

import { AuthContext } from 'app/contexts/Auth';
import useFetchBrandingResource from 'app/hooks/useFetchBrandingResource';

import { brandColorCss, readBrandColorOverride } from './brandColorOverride';

type BrandingStatus = 'loading' | 'branded' | 'unbranded';

interface BrandingContextValue {
  status: BrandingStatus;
  logoUrl: string | null;
  logoOnDarkUrl: string | null;
  isBranded: boolean;
  /** The brand's colours are on screen (or on their way): from the stylesheet, its cache or `?brandColor=`. */
  hasBrandColours: boolean;
  /** Logo URLs that failed to load this visit, so every logo slot falls back together. */
  failedLogoUrls: string[];
  markLogoFailed: (url: string) => void;
}

export const UNBRANDED: BrandingContextValue = {
  status: 'unbranded',
  logoUrl: null,
  logoOnDarkUrl: null,
  isBranded: false,
  hasBrandColours: false,
  failedLogoUrls: [],
  markLogoFailed: () => {},
};

/** The company's own logo for a ground, when it has one that loads; null means show Aslan's. */
export const companyLogoFor = (
  branding: BrandingContextValue,
  variant: 'onLight' | 'onDark'
): string | null => {
  const url = variant === 'onDark' ? branding.logoOnDarkUrl : branding.logoUrl;
  if (url && !branding.failedLogoUrls.includes(url)) {
    return url;
  }
  return null;
};

export const BrandingContext = createContext<BrandingContextValue>(UNBRANDED);

/** Marks the co-brand `<link>`s: two exist briefly while a refreshed URL loads. */
export const CO_BRAND_STYLESHEET_ATTRIBUTE = 'data-co-brand-css';

export const coBrandStylesheets = () =>
  Array.from(
    document.head.querySelectorAll<HTMLLinkElement>(`link[${CO_BRAND_STYLESHEET_ATTRIBUTE}]`)
  );

const removeCoBrandStylesheets = () => coBrandStylesheets().forEach((link) => link.remove());

/** Marks the last stylesheet's CSS, put on screen from the cache until the live one loads. */
export const CACHED_CO_BRAND_ATTRIBUTE = 'data-co-brand-css-cached';
/** Marks the `?brandColor=` override's CSS. */
export const BRAND_COLOR_OVERRIDE_ATTRIBUTE = 'data-brand-color-override';

export const coBrandCacheKey = (companyId: string) => `aslan.co-brand-css.${companyId}`;

const styleElement = (attribute: string) =>
  document.head.querySelector<HTMLStyleElement>(`style[${attribute}]`);

const addStyleElement = (attribute: string, css: string) => {
  const style = styleElement(attribute) ?? document.createElement('style');
  style.setAttribute(attribute, '');
  style.textContent = css;
  document.head.appendChild(style);
};

const removeStyleElement = (attribute: string) => styleElement(attribute)?.remove();

// Storage can be full or blocked (private windows); the cache is a nicety, so failures are ignored
const readCachedCss = (companyId: string) => {
  try {
    return localStorage.getItem(coBrandCacheKey(companyId));
  } catch {
    return null;
  }
};

const writeCachedCss = (companyId: string, css: string) => {
  try {
    localStorage.setItem(coBrandCacheKey(companyId), css);
  } catch {
    // Not cached: the next visit starts in the Aslan colours until the stylesheet loads
  }
};

const clearCachedCss = (companyId: string) => {
  try {
    localStorage.removeItem(coBrandCacheKey(companyId));
  } catch {
    // Nothing to clear
  }
};

/** Keeps the loaded stylesheet's CSS for the next visit. A failed fetch only means no cache. */
const cacheStylesheet = async (companyId: string, stylesheetUrl: string) => {
  try {
    const response = await fetch(stylesheetUrl, { cache: 'no-store' });
    if (response.ok) {
      writeCachedCss(companyId, await response.text());
    }
  } catch {
    // Not cached: the next visit starts in the Aslan colours until the stylesheet loads
  }
};

interface BrandingProviderProps {
  children: ReactNode;
}

/**
 * The signed-in company's branding: its logos and its co-brand stylesheet, each a file-processing
 * resource. Signed out, the portal is Aslan's. A missing or failed file keeps the Aslan look; a
 * failed refetch keeps what is already on screen.
 */
export function BrandingProvider({ children }: BrandingProviderProps) {
  const companyId = useContext(AuthContext)?.company?.id;

  const logo = useFetchBrandingResource('company-logo', companyId);
  const logoOnDark = useFetchBrandingResource('company-logo-dark', companyId);
  const stylesheet = useFetchBrandingResource('co-brand-css', companyId);

  const logoUrl = logo.data ?? null;
  const logoOnDarkUrl = logoOnDark.data ?? null;
  const stylesheetUrl = stylesheet.data ?? null;

  const hasCachedCss = !!(companyId && readCachedCss(companyId));
  const hasNoStylesheet = stylesheet.isSuccess && !stylesheetUrl;
  const stylesheetFailed = stylesheet.isError && !stylesheetUrl;

  // Before paint, so a returning user sees their brand straight away rather than Aslan's colours
  useLayoutEffect(() => {
    // Another company's cached colours never carry over to this one
    removeStyleElement(CACHED_CO_BRAND_ATTRIBUTE);
    if (companyId) {
      const cachedCss = readCachedCss(companyId);
      if (cachedCss && coBrandStylesheets().length === 0) {
        addStyleElement(CACHED_CO_BRAND_ATTRIBUTE, cachedCss);
      }
    }
  }, [companyId]);

  // The stylesheet is cached once per company per visit, not on every refresh of its URL
  const cachedForCompany = useRef<string | null>(null);

  useEffect(() => {
    if (hasNoStylesheet && companyId) {
      // The company has no stylesheet any more, so its cached one must not come back
      clearCachedCss(companyId);
    }
    if (hasNoStylesheet || stylesheetFailed) {
      removeStyleElement(CACHED_CO_BRAND_ATTRIBUTE);
    }
  }, [hasNoStylesheet, stylesheetFailed, companyId]);

  useEffect(() => {
    if (stylesheetUrl) {
      // A refreshed URL is the same file, so the old link stays until the new one has loaded;
      // removing it first would flash the Aslan colours.
      const previous = coBrandStylesheets();
      const link = document.createElement('link');
      link.setAttribute(CO_BRAND_STYLESHEET_ATTRIBUTE, '');
      link.rel = 'stylesheet';
      link.href = stylesheetUrl;
      link.onload = () => {
        previous.forEach((old) => old.remove());
        removeStyleElement(CACHED_CO_BRAND_ATTRIBUTE);
        if (companyId && cachedForCompany.current !== companyId) {
          cachedForCompany.current = companyId;
          cacheStylesheet(companyId, stylesheetUrl);
        }
      };
      link.onerror = () => {
        addBreadcrumb({
          category: 'branding',
          level: 'warning',
          message: 'Co-brand stylesheet failed to load; keeping the styling on screen',
          data: { companyId },
        });
        link.remove();
      };
      document.head.appendChild(link);
    } else {
      removeCoBrandStylesheets();
    }
  }, [stylesheetUrl, companyId]);

  useEffect(
    () => () => {
      removeCoBrandStylesheets();
      removeStyleElement(CACHED_CO_BRAND_ATTRIBUTE);
    },
    []
  );

  const brandColorOverride = useMemo(() => {
    const hex = readBrandColorOverride();
    return hex ? brandColorCss(hex) : null;
  }, []);

  useLayoutEffect(() => {
    if (brandColorOverride) {
      addStyleElement(BRAND_COLOR_OVERRIDE_ATTRIBUTE, brandColorOverride);
    }
    return () => removeStyleElement(BRAND_COLOR_OVERRIDE_ATTRIBUTE);
  }, [brandColorOverride]);

  const isBranded = !!(logoUrl || logoOnDarkUrl || stylesheetUrl);
  const hasBrandColours = !!(
    stylesheetUrl ||
    brandColorOverride ||
    (hasCachedCss && !hasNoStylesheet && !stylesheetFailed)
  );
  const isLoading =
    logo.isInitialLoading || logoOnDark.isInitialLoading || stylesheet.isInitialLoading;

  let status: BrandingStatus = 'unbranded';
  if (isLoading) {
    status = 'loading';
  } else if (isBranded) {
    status = 'branded';
  }

  const [failedLogoUrls, setFailedLogoUrls] = useState<string[]>([]);
  const markLogoFailed = useCallback(
    (url: string) =>
      setFailedLogoUrls((failed) => (failed.includes(url) ? failed : [...failed, url])),
    []
  );

  const value = useMemo<BrandingContextValue>(
    () => ({
      status,
      logoUrl,
      logoOnDarkUrl,
      isBranded,
      hasBrandColours,
      failedLogoUrls,
      markLogoFailed,
    }),
    [status, logoUrl, logoOnDarkUrl, isBranded, hasBrandColours, failedLogoUrls, markLogoFailed]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export const useCompanyBranding = () => useContext(BrandingContext);

/**
 * The fill and text of a selected item (active nav row, chosen tab), and the ink of an unselected
 * row's icon. Unbranded, today's indigo; branded, the action shade, which createBrandVars darkens
 * so white text on it, and it on a light ground, stay readable.
 */
export const useSelectedColours = (unbrandedFill = 'brand.indigo') => {
  const { hasBrandColours } = useCompanyBranding();
  if (hasBrandColours) {
    return { fill: 'action.primary', text: 'text.on-brand', icon: 'icon.brand' };
  }
  return { fill: unbrandedFill, text: 'brand.white', icon: 'brand.indigo' };
};
